import { useEffect, useRef } from "react"
import { DeliveryEntity } from "../../Objects/DeliveryEntity/DeliveryEntity"

const notificationSound = typeof Audio !== "undefined" ? new Audio("/sounds/newOrder.wav") : null

interface UseCourierOfferNotificationsParams {
  activeTab: "panel" | "help" | "financial" | "tutorials" | "notifications"
  isOnline: boolean
  onExternalOffer: (delivery: DeliveryEntity) => void
}

export function useCourierOfferNotifications({
  activeTab,
  isOnline,
  onExternalOffer,
}: UseCourierOfferNotificationsParams) {
  const originalTitleRef = useRef<string>(typeof document !== "undefined" ? document.title : "")

  useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    originalTitleRef.current = document.title
  }, [])

  useEffect(() => {
    if (!isOnline || typeof window === "undefined" || !("Notification" in window)) {
      return
    }

    if (Notification.permission === "default") {
      void Notification.requestPermission()
    }
  }, [isOnline])

  useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    if (activeTab === "panel" && !document.hidden) {
      document.title = originalTitleRef.current
    }
  }, [activeTab])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && activeTab === "panel") {
        document.title = originalTitleRef.current
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [activeTab])

  const notifyNewDelivery = (delivery: DeliveryEntity) => {
    if (!isOnline) {
      return
    }

    const orderId = delivery.orderId > 0 ? `#${delivery.orderId}` : ""

    const customerName = delivery.order?.customerEntity?.personEntity?.name?.trim()

    const bodyParts = [customerName ? `Cliente: ${customerName}` : null, orderId ? `Pedido ${orderId}` : null].filter(
      Boolean,
    )

    try {
      if (notificationSound) {
        notificationSound.currentTime = 0
        void notificationSound.play()
      }
    } catch {}

    const isOutsideMainPanel = activeTab !== "panel" || document.hidden

    if (isOutsideMainPanel) {
      onExternalOffer(delivery)
      document.title = `🔔 Nova oferta - ${originalTitleRef.current}`
    }

    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      const notification = new Notification("Nova oferta de entrega", {
        body: bodyParts.length > 0 ? bodyParts.join(" • ") : "Você recebeu um novo pedido para entrega.",
        icon: "/logo192.png",
        tag: `delivery-offer-${delivery.id}`,
        requireInteraction: isOutsideMainPanel,
      })

      notification.onclick = () => {
        window.focus()
        notification.close()
      }
    }
  }

  return {
    notifyNewDelivery,
  }
}
